<script setup>
import { computed, watch, watchEffect } from 'vue'
import { SUPPLIER_LAYOUTS } from '@supplier/router/routeNames'
import { useSupplierImportStore } from '@supplier/stores/useSupplierImportStore'
import { useSupplierSessionStore } from '@supplier/stores/useSupplierSessionStore'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import CloudflareChallengeHost from '@components/app-shell/CloudflareChallengeHost.vue'
import SupplierConnectStoreModal from '@supplier/components/SupplierConnectStoreModal.vue'
import SupplierSessionError from '@supplier/components/SupplierSessionError.vue'
import SupplierShopifyReauthModal from '@supplier/components/SupplierShopifyReauthModal.vue'
import SupplierBlankLayout from '@supplier/layouts/SupplierBlankLayout.vue'
import SupplierLayout from '@supplier/layouts/SupplierLayout.vue'
import AppIcon from '@ui/app-icon/AppIcon.vue'
import AppNotification from '@ui/app-notification/AppNotification.vue'

const LAYOUT_COMPONENTS = {
  [SUPPLIER_LAYOUTS.HEADER]: SupplierLayout,
  [SUPPLIER_LAYOUTS.BLANK]: SupplierBlankLayout,
}

const route = useRoute()
const { t } = useI18n()
const session = useSupplierSessionStore()
const importStore = useSupplierImportStore()

const layoutComponent = computed(() => {
  if (!route.matched.length) return null
  return LAYOUT_COMPONENTS[route.meta.layout] ?? SupplierLayout
})
const hasSessionError = computed(() => session.loadState === 'error')

watch(() => session.isSupplier, (isSupplier) => {
  if (isSupplier) importStore.start()
}, { immediate: true })

watchEffect(() => {
  const pageTitle = route.meta?.title ? t(route.meta.title) : ''
  const documentTitle = [pageTitle, window.config?.appName].filter(Boolean).join(' · ')
  if (documentTitle) document.title = documentTitle
})
</script>

<template>
  <div class="supplier-app">
    <supplier-session-error v-if="hasSessionError" />
    <component
      :is="layoutComponent"
      v-else-if="layoutComponent"
    >
      <router-view v-slot="{ Component, route: viewRoute }">
        <component
          :is="Component"
          :key="viewRoute.path"
        />
      </router-view>
    </component>
    <div
      v-else
      class="supplier-app-loader"
    >
      <app-icon
        name="Spinner"
        size="32px"
      />
    </div>

    <cloudflare-challenge-host />
    <app-notification />
    <template v-if="session.isSupplier">
      <supplier-shopify-reauth-modal />
      <supplier-connect-store-modal />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.supplier-app-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  color: var(--a-mulberry);
}
</style>
